<template>
  <div ref="LvDropdownTreeCheckboxSelect" class="lv-dropdown-tree-checkbox-select">
    <LvText v-if="title" h6 bold class="lv-dropdown-tree-checkbox-select__title">
      {{ $t(title) }}<LvText v-if="required" red h5>*</LvText>
    </LvText>
    <ValidationProvider
      v-slot="{ errors }"
      class="lv-dropdown-tree-checkbox-select__content-wrap"
      :rules="rules"
    >
      <input type="hidden" :value="value" />
      <div v-click-outside="$_onCloseMenu">
        <div
          class="lv-dropdown-tree-checkbox-select__content"
          :class="{
            outline: outline,
            narrow: narrow,
            wide: wide,
            disabled: disabled,
            'lv-dropdown-tree-checkbox-select__content--error': errors.length > 0,
          }"
        >
          <button class="input-button" @click="$_onClick">
            <LvText h5 class="select">{{ _showText }}</LvText>
            <LvText
              v-if="_selectedLeafAmount > 0"
              h5
              class="num"
              :class="{ 'has-cancel-icon': _isDisplayCancelIcon }"
              >{{ _selectedLeafAmount }}</LvText
            >
            <i
              v-if="_isDisplayCancelIcon"
              class="cancel-icon icon-cancel"
              @click.stop="$_onCancel"
            />
          </button>
          <i
            v-if="outline || disabled"
            class="icon trailing-icon outline icon-chevron-up"
            :class="{ show: dropdownMenuShow }"
          />
          <i
            v-else-if="!outline && !dropdownMenuShow"
            class="icon trailing-icon icon-arrow-drop-down-circle"
          />
          <i
            v-else-if="!outline && dropdownMenuShow"
            class="icon trailing-icon icon-arrow-drop-up-circle"
          />
          <transition mode="out-in">
            <div v-if="dropdownMenuShow" class="lv-dropdown-menu" :class="{ 'drop-up': isDropUp }">
              <SearchInput
                v-if="hasSearch"
                :value="searchValue"
                placeholder="搜尋"
                class="pl-8 pt-8 pr-8"
                @input="$_onSearch($event)"
              />
              <perfect-scrollbar class="ps lv-dropdown-scrollable-content">
                <WsTextChip001
                  v-if="remindText && _filteredMenu.length > 0"
                  thin
                  :textLabel="remindText"
                  :color="remindTextColor"
                  :leadingIcon="remindTextIcon"
                  class="pl-12 pt-12"
                />
                <template v-for="(topNode, topNodeIndex) in _filteredMenu">
                  <div
                    v-if="topNode.items && topNode.items.length"
                    :key="topNode.id"
                    class="classification-list"
                    :class="{ 'pb-12': topNodeIndex === _filteredMenu.length - 1 }"
                  >
                    <LvFlexBox class="mb-8">
                      <LvText h5 bold>{{ $t(topNode.title) }}</LvText>
                      <button
                        v-if="!$_isDisableCheckbox(topNode) && !searchValue"
                        @click.stop="$_onToggleTopSelectAll(topNode)"
                      >
                        <LvText h5 blue>{{ $t($_getTopButtonText(topNode.id)) }}</LvText>
                      </button>
                    </LvFlexBox>
                    <LvDivider marginTop="0" marginBottom="8px" />
                    <div class="classification-subtitle">
                      <LvDropdownTreeCheckboxNode
                        :nodes="topNode.items"
                        :checkedStateMap="_checkedStateMap"
                        :disableCheckbox="$_isDisableCheckbox(topNode)"
                        @toggle="$_onToggleNode"
                      />
                    </div>
                  </div>
                </template>
                <LvText v-if="_filteredMenu.length === 0" h5 class="ml-12 my-8">{{
                  $t('無選項')
                }}</LvText>
              </perfect-scrollbar>
            </div>
          </transition>
        </div>
      </div>
      <div
        v-if="!noErrorText && errors && errors.length > 0"
        class="lv-dropdown-tree-checkbox-select__invalid-text"
      >
        <i class="icon-warning lv-dropdown-tree-checkbox-select__invalid-icon" />
        <p
          v-for="(text, textIndex) in errors"
          :key="textIndex"
          class="lv-dropdown-tree-checkbox-select__invalid-text-item"
        >
          {{ $t(text) }}
        </p>
      </div>
    </ValidationProvider>
  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside';
import { ValidationProvider } from 'vee-validate';
import { extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import LvDropdownTreeCheckboxNode from '@/__common/baseComponents/LvDropdownTreeCheckboxNode.vue';
import H_common from '@/helpers/common';

extend('required', {
  ...required,
  message: '此項目為必填',
});

export default {
  name: 'LvDropdownTreeCheckboxSelect',
  directives: {
    ClickOutside,
  },
  components: {
    ValidationProvider,
    LvDropdownTreeCheckboxNode,
  },
  props: {
    /**
     * Dropdown 標題。
     * @type {String}
     */
    title: {
      type: String,
      default: '',
    },
    /**
     * 是否為必填欄位。
     * @type {Boolean}
     */
    required: {
      type: Boolean,
      default: false,
    },
    /**
     * 是否停用。
     * @type {Boolean}
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 輪廓樣式開關。
     * @type {Boolean}
     */
    outline: {
      type: Boolean,
      default: true,
    },
    /**
     * 窄版寬度樣式開關。
     * @type {Boolean}
     */
    narrow: {
      type: Boolean,
      default: false,
    },
    /**
     * 寬版寬度樣式開關。
     * @type {Boolean}
     */
    wide: {
      type: Boolean,
      default: false,
    },
    /**
     * 未選取時顯示文字。
     * @type {String}
     */
    placeholder: {
      type: String,
      default: '選擇',
    },
    /**
     * 多層節點資料，節點格式為 `{ id, title, items }`。
     * @type {Array}
     * @example
     * [
     *   {
     *     id: 'domain-custom',
     *     title: '自訂領域',
     *     items: [
     *       {
     *         id: 'domain-environment',
     *         title: '環保',
     *         items: [{ id: 'air-control', title: '空氣污染防制', items: [] }],
     *       },
     *     ],
     *   },
     * ]
     */
    menu: {
      type: Array,
      default() {
        return [];
      },
    },
    /**
     * 已選值，與 `input` 事件 payload 同格式。
     * 僅包含被選子樹，節點格式為 `{ id, items }`。
     * 若選單上該節點仍有子層，但 `items` 為空陣列，表示僅勾選父層（不展開子葉）。
     * @type {Array}
     * @example
     * [
     *   {
     *     id: 'domain-custom',
     *     items: [
     *       {
     *         id: 'domain-environment',
     *         items: [{ id: 'air-control', items: [] }],
     *       },
     *     ],
     *   },
     * ]
     */
    value: {
      type: Array,
      default() {
        return [];
      },
    },
    scroll: {
      type: Number,
    },
    remindText: {
      type: String,
    },
    remindTextColor: {
      type: String,
      default: 'red',
    },
    remindTextIcon: {
      type: String,
      default: 'icon-warning',
    },
    rules: {
      type: String,
      default: '',
    },
    hasCancelIcon: {
      type: Boolean,
      default: false,
    },
    noErrorText: {
      type: Boolean,
      default: false,
    },
    topNodeDisableList: {
      type: Array,
      default() {
        return [];
      },
    },
    hasSearch: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      dropdownMenuShow: false,
      isDropUp: false,
      dropdownHeight: 0,
      dropdownTop: 0,
      searchValue: '',
    };
  },
  watch: {
    scroll() {
      this.$_getDropdownHeight();
      this.$_isDropUp();
    },
  },
  created() {
    window.addEventListener('resize', this.$_getDropdownHeight);
    window.addEventListener('resize', this.$_isDropUp);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.$_getDropdownHeight);
    window.removeEventListener('resize', this.$_isDropUp);
  },
  mounted() {
    this.$_getDropdownHeight();
    this.$_isDropUp();
  },
  computed: {
    _menuNodeMap() {
      const nodeMap = {};
      const traverse = nodes => {
        nodes.forEach(node => {
          nodeMap[node.id] = node;
          traverse(node.items || []);
        });
      };
      traverse(this.menu);
      return nodeMap;
    },
    _leafIdsMap() {
      const leafMap = {};
      const walk = node => {
        if (!node.items || node.items.length === 0) {
          leafMap[node.id] = [node.id];
          return leafMap[node.id];
        }
        let leafIds = [];
        node.items.forEach(child => {
          leafIds = [...leafIds, ...walk(child)];
        });
        leafMap[node.id] = leafIds;
        return leafIds;
      };
      this.menu.forEach(node => walk(node));
      return leafMap;
    },
    _parentOnlyBranchIdSet() {
      const set = new Set();
      const walk = nodes => {
        (nodes || []).forEach(n => {
          const ch = n.items || [];
          if (ch.length === 0) {
            const menuNode = this._menuNodeMap[n.id];
            if (menuNode && (menuNode.items || []).length > 0) {
              set.add(n.id);
            }
            return;
          }
          walk(ch);
        });
      };
      walk(this.value);
      return set;
    },
    _selectedLeafIdSet() {
      const selectedLeafSet = new Set();
      const addNodeLeafIds = valueNode => {
        const valueChildren = valueNode.items || [];
        if (valueChildren.length > 0) {
          valueChildren.forEach(child => addNodeLeafIds(child));
          return;
        }
        const menuNode = this._menuNodeMap[valueNode.id];
        if (menuNode && (menuNode.items || []).length > 0) {
          return;
        }
        const leafIds = this._leafIdsMap[valueNode.id] || [];
        leafIds.forEach(leafId => selectedLeafSet.add(leafId));
      };
      (this.value || []).forEach(node => addNodeLeafIds(node));
      return selectedLeafSet;
    },
    _checkedStateMap() {
      const parentOnlySet = this._parentOnlyBranchIdSet;
      const selectedSet = this._selectedLeafIdSet;
      const checkedStateMap = {};
      Object.keys(this._leafIdsMap).forEach(nodeId => {
        const leafIds = this._leafIdsMap[nodeId] || [];
        const selectedLeafAmount = leafIds.filter(leafId => selectedSet.has(leafId)).length;
        const isParentOnlySelf = parentOnlySet.has(nodeId);
        const hasParentOnlyDescendant = [...parentOnlySet].some(pid => {
          if (pid === nodeId) return false;
          const pLeaves = this._leafIdsMap[pid] || [];
          return pLeaves.length > 0 && pLeaves.every(l => leafIds.includes(l));
        });
        const checked =
          isParentOnlySelf || (leafIds.length > 0 && selectedLeafAmount === leafIds.length);
        const indeterminate =
          !checked &&
          ((selectedLeafAmount > 0 && selectedLeafAmount < leafIds.length) ||
            hasParentOnlyDescendant);
        checkedStateMap[nodeId] = {
          checked,
          indeterminate,
        };
      });
      return checkedStateMap;
    },
    _selectedLeafAmount() {
      return this._selectedLeafIdSet.size;
    },
    _showText() {
      if (this._selectedLeafAmount === 0) {
        return this.$t(this.placeholder);
      }
      const selectedTitles = [];
      Object.keys(this._leafIdsMap).forEach(nodeId => {
        const leafIds = this._leafIdsMap[nodeId] || [];
        const isLeaf = leafIds.length === 1 && leafIds[0] === nodeId;
        if (!isLeaf || !this._selectedLeafIdSet.has(nodeId)) {
          return;
        }
        const node = this._menuNodeMap[nodeId];
        if (node) {
          selectedTitles.push(this.$t(node.title));
        }
      });
      return selectedTitles.join('、');
    },
    _isDisplayCancelIcon() {
      return this.hasCancelIcon && !this.disabled && this._selectedLeafAmount > 0;
    },
    _filteredMenu() {
      const keyword = this.searchValue.trim().toLowerCase();
      if (!keyword) {
        return this.menu;
      }
      return this.$_filterMenu(this.menu, keyword);
    },
  },
  methods: {
    $_onCancel() {
      this.$emit('input', []);
    },
    $_onClick() {
      if (this.disabled) return;
      const willClose = this.dropdownMenuShow;
      this.dropdownMenuShow = !this.dropdownMenuShow;
      if (willClose) {
        this.searchValue = '';
      }
      this.$_getDropdownHeight();
      this.$_isDropUp();
    },
    $_onCloseMenu() {
      this.dropdownMenuShow = false;
      this.searchValue = '';
    },
    $_onSearch(value) {
      this.searchValue = value;
    },
    $_matchTitle(title, keyword) {
      return this.$t(title).toLowerCase().includes(keyword);
    },
    $_filterTreeItems(items, keyword) {
      return (items || []).reduce((acc, node) => {
        if (this.$_matchTitle(node.title, keyword)) {
          acc.push(node);
          return acc;
        }
        const filteredChildren = this.$_filterTreeItems(node.items || [], keyword);
        if (filteredChildren.length > 0) {
          acc.push({ ...node, items: filteredChildren });
        }
        return acc;
      }, []);
    },
    $_filterMenu(menu, keyword) {
      return (menu || []).reduce((acc, topNode) => {
        if (this.$_matchTitle(topNode.title, keyword)) {
          if (topNode.items && topNode.items.length) {
            acc.push(topNode);
          }
          return acc;
        }
        const filteredItems = this.$_filterTreeItems(topNode.items || [], keyword);
        if (filteredItems.length > 0) {
          acc.push({ ...topNode, items: filteredItems });
        }
        return acc;
      }, []);
    },
    $_onToggleNode(node) {
      const isParentOnlyNode = this._parentOnlyBranchIdSet.has(node.id);
      const isChecked = this._checkedStateMap[node.id] && this._checkedStateMap[node.id].checked;
      if (isChecked && isParentOnlyNode) {
        this.$emit('input', this.$_removeParentOnlyBranch(this.value, node.id));
        return;
      }
      const nextSelectedLeafSet = new Set(this._selectedLeafIdSet);
      const leafIds = this._leafIdsMap[node.id] || [];
      if (isChecked) {
        leafIds.forEach(leafId => nextSelectedLeafSet.delete(leafId));
      } else {
        leafIds.forEach(leafId => nextSelectedLeafSet.add(leafId));
      }
      this.$_emitInput(nextSelectedLeafSet);
    },
    $_onToggleTopSelectAll(topNode) {
      const nextSelectedLeafSet = new Set(this._selectedLeafIdSet);
      const leafIds = this._leafIdsMap[topNode.id] || [];
      const isChecked =
        this._checkedStateMap[topNode.id] && this._checkedStateMap[topNode.id].checked;
      if (isChecked) {
        leafIds.forEach(leafId => nextSelectedLeafSet.delete(leafId));
        this.$_emitInput(nextSelectedLeafSet, { stripParentOnlyTopId: topNode.id });
        return;
      }
      leafIds.forEach(leafId => nextSelectedLeafSet.add(leafId));
      this.$_emitInput(nextSelectedLeafSet);
    },
    $_emitInput(selectedLeafSet, options = {}) {
      const nextValue = this.$_buildSelectedTree(this.menu, selectedLeafSet);
      const merged = this.$_mergePreservedParentOnly(
        nextValue,
        selectedLeafSet,
        options.stripParentOnlyTopId,
      );
      this.$emit('input', merged);
    },
    $_mergePreservedParentOnly(treeFromLeaves, selectedLeafSet, stripParentOnlyTopId) {
      const oldParentOnly = this._parentOnlyBranchIdSet;
      const keep = new Set();
      oldParentOnly.forEach(pid => {
        const pLeaves = this._leafIdsMap[pid] || [];
        if (pLeaves.some(lid => selectedLeafSet.has(lid))) {
          return;
        }
        if (stripParentOnlyTopId) {
          const stripLeaves = this._leafIdsMap[stripParentOnlyTopId] || [];
          if (pLeaves.length && pLeaves.every(l => stripLeaves.includes(l))) {
            return;
          }
        }
        keep.add(pid);
      });
      if (!keep.size) {
        return treeFromLeaves;
      }
      return this.$_injectParentOnlyBranches(this.menu, treeFromLeaves, keep);
    },
    $_findMenuPathNodes(nodes, targetId, acc = []) {
      for (const node of nodes || []) {
        const chain = [...acc, node];
        if (node.id === targetId) {
          return chain;
        }
        const found = this.$_findMenuPathNodes(node.items || [], targetId, chain);
        if (found) {
          return found;
        }
      }
      return null;
    },
    $_injectParentOnlyBranches(menu, tree, keepSet) {
      const next = JSON.parse(JSON.stringify(tree || []));
      keepSet.forEach(pid => {
        const path = this.$_findMenuPathNodes(menu, pid);
        if (!path || path.length < 2) {
          return;
        }
        let level = next;
        for (let i = 0; i < path.length; i += 1) {
          const menuNode = path[i];
          const isLast = i === path.length - 1;
          let existing = level.find(n => n.id === menuNode.id);
          if (!existing) {
            existing = { id: menuNode.id, items: [] };
            level.push(existing);
          }
          if (isLast) {
            existing.items = [];
            return;
          }
          if (!existing.items) {
            existing.items = [];
          }
          level = existing.items;
        }
      });
      return next;
    },
    $_removeParentOnlyBranch(value, nodeId) {
      const walk = nodes =>
        (nodes || []).flatMap(n => {
          const kids = n.items || [];
          const menuNode = this._menuNodeMap[n.id];
          const isBranchTerminal =
            kids.length === 0 && menuNode && (menuNode.items || []).length > 0;
          if (n.id === nodeId && isBranchTerminal) {
            return [];
          }
          const nextItems = walk(kids);
          return [{ ...n, items: nextItems }];
        });
      return walk(value || []);
    },
    $_buildSelectedTree(nodes, selectedLeafSet) {
      const selectedTree = [];
      nodes.forEach(node => {
        const leafIds = this._leafIdsMap[node.id] || [];
        const hasSelection = leafIds.some(leafId => selectedLeafSet.has(leafId));
        if (!hasSelection) return;
        const children = node.items || [];
        if (children.length === 0) {
          selectedTree.push({
            id: node.id,
            items: [],
          });
          return;
        }
        const selectedChildren = this.$_buildSelectedTree(children, selectedLeafSet);
        selectedTree.push({
          id: node.id,
          items: selectedChildren,
        });
      });
      return selectedTree;
    },
    $_getTopButtonText(topNodeId) {
      const topState = this._checkedStateMap[topNodeId];
      if (topState && topState.checked) {
        return '全取消';
      }
      return '全選';
    },
    $_getDropdownHeight() {
      const { height, y } = H_common.getComponentHeightAndY(
        this.$refs.LvDropdownTreeCheckboxSelect,
      );
      this.dropdownHeight = height;
      this.dropdownTop = y;
    },
    $_isDropUp() {
      this.isDropUp = H_common.isDropUp(this.dropdownHeight, this.dropdownTop, 450, 450);
    },
    $_isDisableCheckbox(topNode) {
      if (!this.topNodeDisableList.length) return false;
      return this.topNodeDisableList.includes(topNode.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.lv-dropdown-tree-checkbox-select {
  width: 100%;
  position: relative;

  &__title {
    margin-bottom: 8px;
  }

  &__content {
    @include ts;
    position: relative;
    border: 1px solid #d9d9d9;
    border-radius: 10px;
    min-height: 46px;
    background: var(--blue-700);

    .icon {
      color: var(--white);
    }

    &.outline {
      border: solid 1px var(--gray-300);
      background-color: transparent;

      .input-button {
        color: var(--gray-700);
      }

      .icon {
        color: var(--gray-500);
      }

      &:hover {
        border: solid 1px #71b6eb;
        background-color: var(--white);

        .input-button {
          color: var(--blue-500);
        }

        .icon {
          color: var(--blue-500);
        }
      }

      &.lv-dropdown-tree-checkbox-select__content--error {
        border: solid 1px var(--red-500);
        background-color: rgba(206, 56, 41, 0.1);

        .input-button,
        .icon {
          color: var(--red-500);
        }

        ::v-deep .select.lv-text {
          color: var(--red-500);
        }

        &:hover {
          border: solid 1px var(--red-500);
          background-color: rgba(206, 56, 41, 0.1);

          .input-button,
          .icon {
            color: var(--red-500);
          }
        }
      }
    }

    &.disabled {
      pointer-events: none;
      background: #f5f5f5;

      .icon {
        color: var(--gray-500);
      }
    }

    &--error {
      background-color: rgba(206, 56, 41, 0.1);
      border: solid 1px var(--red-500);

      .input-button,
      .icon {
        color: var(--red-500);
      }

      ::v-deep .select.lv-text {
        color: var(--red-500);
      }
    }
  }

  &__content-wrap {
    display: block;
  }

  &__invalid-text {
    @include flex_align;
    @include auto_margin_top(8px);
  }

  &__invalid-icon {
    font-size: 1rem;
    color: var(--red-500);
  }

  &__invalid-text-item {
    @include auto_margin_left(4px);
    font-size: 0.75rem;
    line-height: 1.5;
    color: var(--red-500);
  }
}

.input-button {
  @include ts;
  width: 100%;
  min-height: 46px;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 10px 0 16px;
  position: relative;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  line-height: 1.43;
  letter-spacing: 1px;
  color: var(--white);
}

.select {
  display: block;
  width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  position: absolute;
  color: inherit;
}

.num {
  padding: 2px 9px;
  position: absolute;
  right: 40px;
  line-height: 1.5;
  color: var(--white);
  border-radius: 22px;
  background-color: var(--blue-500);

  &.has-cancel-icon {
    right: 55px;
  }
}

.cancel-icon {
  position: absolute;
  right: 30px;
  font-size: 1.5rem;
  color: var(--gray-700);

  &:hover {
    color: var(--blue-700);
  }
}

.icon {
  @include ts;
  position: absolute;
  top: 10px;
  font-size: 24px;
  color: var(--white);
  pointer-events: none;

  &.trailing-icon {
    right: 8px;

    &.outline {
      @include tf_scxy(1, -1);

      &.show {
        @include tf_scxy(1, 1);
      }
    }
  }
}

.lv-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 100%;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  z-index: 300;

  &.drop-up {
    top: initial;
    bottom: calc(100% + 8px);
  }

  .search-tool {
    padding: 10px;
  }
}

.lv-dropdown-scrollable-content {
  max-height: 320px;
  overflow: hidden;
}

.classification-list {
  padding: 8px 12px 0px;
}

.classification-subtitle {
  padding-left: 4px;
}
</style>
